const DEFAULT_GAP = 10;

/**
 * Auto place slab placeholder / slab image to avoid overlaps with existing placeholders and slab images.
 * Strategy:
 *  - Try to place to the right of the combined occupied bounds (+gap).
 *
 * @param {Object} params
 * @param {Object} params.shape - New shape to place
 * @param {Object[]} params.existingPlaceholders - Existing placeholder shapes
 * @param {Object[]} params.existingSlabImages - Existing slab image shapes
 * @param {number} [params.gap=20] - Space between shapes in world units
 */
export function autoPlaceSlab({
                                shape,
                                existingPlaceholders = [],
                                existingSlabImages = [],
                                gap = DEFAULT_GAP,
                              }) {
  if (!shape) return;

  const existing = [...existingPlaceholders, ...existingSlabImages].filter(Boolean);
  if (existing.length === 0) return;

  const occupied = getCombinedBoundsWorld(existing);
  const me = getShapeBoundsWorld(shape);
  const myPos = getShapePosition(shape); // current position in world coords

  // Place to the right of all existing entities
  const targetWorldMinX = occupied.maxX + gap;
  const targetWorldMinY = occupied.minY;

  // How much we need to move in world coords
  const dxWorld = targetWorldMinX - me.minX;
  const dyWorld = targetWorldMinY - me.minY;

  if (!dxWorld && !dyWorld) return;

  shape.setSlabPosition({
    x: myPos.x + dxWorld,
    y: myPos.y + dyWorld,
  });
}

/**
 * Returns shape position in world coords.
 * Adjust this if your API differs.
 */
function getShapePosition(shape) {
  const pos = shape.getPosition?.() ?? shape.position ?? { x: 0, y: 0 };
  return {
    x: Number(pos.x) || 0,
    y: Number(pos.y) || 0,
  };
}

/**
 * Returns world bounds for a shape when line points are LOCAL to shape position.
 * Works with rotation as long as the local points already represent the rotated geometry.
 */
function getShapeBoundsWorld(shape) {
  const lines = shape.getLines?.() ?? [];
  const pos = getShapePosition(shape);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const ln of lines) {
    const pts = [ln?.from, ln?.to].filter(Boolean);
    for (const p of pts) {
      const wx = pos.x + p.x;
      const wy = pos.y + p.y;

      if (wx < minX) minX = wx;
      if (wy < minY) minY = wy;
      if (wx > maxX) maxX = wx;
      if (wy > maxY) maxY = wy;
    }
  }

  if (!Number.isFinite(minX)) minX = pos.x;
  if (!Number.isFinite(minY)) minY = pos.y;
  if (!Number.isFinite(maxX)) maxX = pos.x;
  if (!Number.isFinite(maxY)) maxY = pos.y;

  return { minX, minY, maxX, maxY };
}

function getCombinedBoundsWorld(shapes) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const s of shapes) {
    const b = getShapeBoundsWorld(s);
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }

  return { minX, minY, maxX, maxY };
}
